import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import type { SortingRule } from "react-table";
import useSWR from "swr";
import { ArrayParam, StringParam, useQueryParams } from "use-query-params";
import { FiltersArea } from "../../../../../../components/Filters/Filters";
import { TablePageLayout } from "../../../../../../components/TablePage/TablePageLayout";
import type { TableRefProps } from "../../../../../../components/Table/utils";
import { endpoints } from "../../../../../../endpoints";
import type {
  OptionType,
  TagClassificationConfig,
} from "../../../../../../types/types";
import type {
  PIMProductBase,
  PIMProductBasePaginagedOutput,
} from "../../../../../../types/types.PIM";
import { useDebounce } from "../../../../../../util/hooks";
import {
  defaultHandleSort,
  DEFAULT_PER_PAGE,
  TablePlaceholder,
  updateCustomLabels,
  useStoreState,
  useSupportedLanguages,
} from "../../../../../../util/util";
import type { Filters } from "../../../../../../util/filterStateManager";
import {
  applyFiltersToURL,
  loadFilters,
} from "../../../../../../util/filterStateManager";
import { createChangePerPageHandler } from "../../../../../../components/Pagination/util";
import type { ConstructQuery } from "../../ProductsList.util";
import type {
  AsyncTableType,
  UploadState,
} from "./createProductFromUploads.util";
import { useAuthContext } from "../../../../../../components/Auth";
import { DEFAULT_STATUSES } from "./createProductFromUploads.util";
import { TableWithBulkAction } from "../../../../../../components/Table/TableWithBulkAction";

const PER_PAGE_ITEMS = [10, 20, 50];

const PRODUCT_LIST_QUERY_PARAMS = {
  template: StringParam,
  q: StringParam,
  status: ArrayParam,
};

/**
 * Products list page (e.g. manage > products).  Used for seller admin.
 */
export function CreateProductsList({
  handleSelectRows,
  selectedTemplate,
  defaultSelectedRows,
}: {
  handleSelectRows: (rows: UploadState["selected_products"]) => void;
  selectedTemplate: OptionType<string>;
  defaultSelectedRows: UploadState["selected_products"];
}) {
  const [query, setQuery] = useQueryParams(PRODUCT_LIST_QUERY_PARAMS);

  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "asc",
  });
  const [filtersFromUrl, setFiltersFromUrl] = useState<Filters>();
  const [tableData, setTableData] = useState<AsyncTableType[]>([]);
  // searchQuery is loaded from the URL if q exists.
  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 2000);
  const [offset, setOffset] = useState(0);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
    totalCount: 0,
  });
  const { t } = useTranslation();
  const { supportedLanguageOptions } = useSupportedLanguages();
  const { storefront_id, tenant_id } = useStoreState();
  const location = useLocation();
  const { pathname } = location;
  const history = useHistory();
  const resetSelectedRowsRef = useRef<TableRefProps>(null);
  const { hasPermission } = useAuthContext();

  const hasEditOrPublishPermission =
    hasPermission("modify_products") || hasPermission("publish_products");

  const { data: customLabels, error: customLabelsError } = useSWR<
    TagClassificationConfig[]
  >(
    storefront_id
      ? endpoints.v2_storefronts_id_products_filters_customLabels(storefront_id)
      : null,
    { revalidateOnFocus: true }
  );

  updateCustomLabels(customLabels, supportedLanguageOptions);

  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );

  /**
   * construct query from selected filters,
   * add search term if it exists
   */
  function constructQuery({
    baseUrl,
    query,
    filtersFromUrl,
  }: ConstructQuery<PIMProductBase>) {
    const paramsWithOffset = new URLSearchParams(
      `offset=${offset}&limit=${perPage}&template=${selectedTemplate.label}`
    );

    DEFAULT_STATUSES.forEach((status) => {
      paramsWithOffset.append("status", status);
    });

    if (query) paramsWithOffset.append("q", query);
    paramsWithOffset.append("order_by", sortingRules.orderBy || "asc");
    if (sortingRules.sortBy) {
      paramsWithOffset.append("sort_by", sortingRules.sortBy);
    }
    paramsWithOffset.append(
      "show_inactive",
      hasEditOrPublishPermission ? "true" : "false"
    );
    if (!hasEditOrPublishPermission) {
      paramsWithOffset.append("published_products_only", "true");
    }
    return (
      baseUrl +
      "?" +
      applyFiltersToURL({
        filtersFromUrl,
        params: paramsWithOffset,
      })
    );
  }

  const { data: productsResponse, error: productsError } =
    useSWR<PIMProductBasePaginagedOutput>(
      constructQuery({
        baseUrl: endpoints.v2_tenants_id_pim_products_summary(tenant_id),
        query: debouncedSearchQuery,
        filtersFromUrl,
      })
    );

  const isLoading = !productsResponse && !productsError;

  const tableColumns = useMemo(
    () => [
      {
        header: t("Product ID"),
        accessorKey: "externalId",
        id: "externalId",
        enableSorting: false,
      },
      {
        header: t("Product Name"),
        accessorKey: "name",
        id: "name",
      },
      {
        header: t("Chemical Name"),
        accessorKey: "chemical_name",
        id: "chemical_name",
      },
    ],
    [t]
  );

  const handleClearFilter = () => {
    const destination = pathname;
    history.replace(destination);
    setQuery({
      q: query.q,
      template: selectedTemplate.label,
      status: DEFAULT_STATUSES,
    });
    setOffset(0);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(0);
    setSearchQuery(e.target.value);
  };
  const handleClearSearch = () => {
    setSearchQuery("");
    setQuery({ q: undefined });
    setOffset(0);
  };

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const onResetRows = () => {
    resetSelectedRowsRef.current?.resetSelectedRows();
  };

  useEffect(() => {
    const filters = loadFilters(location);
    setFiltersFromUrl(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname]);

  useEffect(() => {
    const handleProductsData = ({
      data,
      pagination,
    }: PIMProductBasePaginagedOutput) => {
      const tableData = data.map(
        ({
          id,
          name,
          primary_staged_product_id,
          product_number,
          chemical_name,
        }) => ({
          id: product_number ?? primary_staged_product_id ?? id,
          externalId: product_number ?? "--",
          name: name ?? "--",
          chemical_name: chemical_name ?? "--",
        })
      );
      let is_data_changed = false;
      setTableData((prev) => {
        is_data_changed =
          prev.length !== tableData.length ||
          prev.some((row, index) => row.id !== tableData[index]?.id);
        return tableData;
      });
      setTablePagination((prev) => {
        const cur = {
          perPage: perPage,
          pageCount: Math.ceil(pagination.total / perPage),
          pageIndex: pagination.offset / perPage + 1,
          totalCount: pagination.total,
        };
        if (is_data_changed && prev.pageIndex === cur.pageIndex) {
          onResetRows();
        }
        return cur;
      });
    };

    if (productsResponse) {
      const { data: products, pagination } = productsResponse;
      handleProductsData({ data: products, pagination });
    }
  }, [productsResponse, perPage]);

  useEffect(() => {
    setQuery({
      template: selectedTemplate.label,
      status: DEFAULT_STATUSES,
    });
  }, [selectedTemplate.label, setQuery]);

  useEffect(() => {
    if (query.q && query.q !== searchQuery) {
      setSearchQuery(query.q);
    }
  }, [query.q, searchQuery]);

  return (
    <TablePageLayout
      containerStyle={{ marginTop: "30px" }}
      // Toolbar
      searchQuery={searchQuery}
      searchPlaceholder={t("Search by product name or CAS#")}
      onSearchChange={handleSearch}
      onSearchClear={handleClearSearch}
      // Filters
      filtersSlot={
        !customLabelsError ? (
          <FiltersArea
            resetOffset={setOffset}
            customLabels={customLabels ?? []}
            clearFilter={handleClearFilter}
          />
        ) : undefined
      }
      // Meta row
      totalCount={tablePagination.totalCount}
      totalCountLabel={t("products")}
      perPageItems={PER_PAGE_ITEMS}
      perPage={perPage}
      onPerPageChange={changePerPage}
      // Table
      tableSlot={
        <TableWithBulkAction
          columns={tableColumns}
          data={tableData}
          isLoading={isLoading}
          error={productsError}
          handleSort={handleSort}
          handleSelectRows={handleSelectRows}
          defaultSelectedRows={defaultSelectedRows}
          bulkActionElement={() => <></>}
          ref={resetSelectedRowsRef}
          bulkSelectLimit={500}
          Placeholder={<TablePlaceholder />}
        />
      }
      // Pagination
      pagination={tablePagination}
      offset={offset}
      onPageChange={changePage}
    />
  );
}
